import RecurringJobsArtwork from "@/assets/images/recurring/artwork.svg";
import {
  borderWidth,
  colors,
  radius,
  shadows,
  sizes,
  spacings,
} from "@/assets/themes";
import {
  Avatar,
  Block,
  Body14,
  Body16,
  Button,
  H2,
  Icon,
  List,
  Modal,
} from "@/components";
import { CardBase } from "@/components/Styles/Base";
import { BUTTON } from "@/components/Styles/variants";
import { useBreakpoints } from "@/modules/hooks";
import polyglot from "@/utils/polyglot";
import { YOOJO_COVER_PRICE } from "@/utils";
import { RECURRING_JOBS_NEW_PATH } from "../dashboard/config/routes";

const ADVANTAGES = [
  {
    iconName: "refresh",
    title: polyglot.t("recurring_advantages.automate_your_services"),
    description: polyglot.t(
      "recurring_advantages.organize_requests_efficiently"
    ),
  },
  {
    iconName: "settings",
    title: polyglot.t("recurring_advantages.manage_conditions"),
    description: polyglot.t("recurring_advantages.choose_date_schedule_rate"),
  },
  {
    iconName: "credit-card",
    title: polyglot.t("recurring_advantages.simplify_payments", {
      value: polyglot.toSmartCurrency(0, {
        noDecimal: true,
      }),
    }),
    description: polyglot.t("recurring_advantages.pay_provider_online"),
  },
  {
    iconName: "shield-half",
    title: polyglot.t("recurring_advantages.enjoy_yoojo_cover"),
    description: polyglot.t("recurring_advantages.damage_coverage", {
      amount: polyglot.toSmartCurrency(YOOJO_COVER_PRICE, {
        noDecimal: true,
      }),
    }),
  },
];

const RecurringJobsDiscoverModal = ({ isOpen, close, jobber, refuseUrl }) => {
  const breakpoints = useBreakpoints();
  const avatarSize = breakpoints.get({ xs: "150px", md: "180px" });
  const bannerHeight = breakpoints.get({ xs: "160px", md: "200px" });

  return (
    <Modal.Small
      fullScreenOnMobile
      isOpen={isOpen}
      onClose={close}
      css={`
        [aria-label="dialog"] {
          position: relative;
          overflow: hidden;
        }
      `}
    >
      <Block
        position="absolute"
        top="0px"
        right="0px"
        margin={spacings.m}
        zIndex="10"
      >
        <Button.Small
          onClick={close}
          kind={BUTTON.KIND.MINIMAL}
          shape={BUTTON.SHAPE.CIRCLE}
        >
          <Icon.Medium name="times" />
        </Button.Small>
      </Block>
      <Block position="relative" paddingTop={bannerHeight}>
        <Block
          position="absolute"
          top="0px"
          left="-1%"
          right="0px"
          height="250px"
          // fix 1px in x border.
          width="102%"
          backgroundImage={`url(${RecurringJobsArtwork})`}
          backgroundSize="cover"
          backgroundPosition="center"
        />
        <Block
          position="relative"
          zIndex="2"
          borderTopLeftRadius={radius.l}
          borderTopRightRadius={radius.l}
          backgroundColor={colors.background}
          paddingTop={spacings.xl}
        >
          <Block
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
            height="0px"
          >
            <Block
              translateY="0%"
              position="absolute"
              bottom="0px"
              zIndex="10"
              marginX="auto"
              borderRadius={radius.circle}
              backgroundColor={colors.backgroundBlur}
              backdropBlur={20}
              border={`${borderWidth.l} solid ${colors.background}`}
            >
              {jobber?.avatar ? (
                <Avatar
                  size={avatarSize}
                  src={jobber.avatar}
                  name={jobber.first_name}
                />
              ) : (
                <Block
                  width={avatarSize}
                  height={avatarSize}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon.Large size={sizes.size96} name="refresh" />
                </Block>
              )}
            </Block>
          </Block>
          <Modal.Item.Wrapper>
            <Block
              display="flex"
              flexDirection="column"
              gap={spacings.m}
              marginTop={spacings.sm}
            >
              <Block>
                <H2 align="center">
                  {polyglot.t("recurring.automatise_recurring")}
                </H2>
                <Body16 align="center">
                  {polyglot.t("recurring.automatise_recurring_description")}
                </Body16>
              </Block>
              <CardBase
                light
                flat
                css={`
                  padding-top: ${spacings.xs};
                  padding-bottom: ${spacings.xs};
                `}
              >
                {ADVANTAGES.map(({ iconName, title, description }) => (
                  <List.Item
                    key={`recurring-jobs-discover-modal-advantage-${iconName}`}
                    withGutters
                    divider={false}
                    LeftComponent={() => (
                      <Block
                        border={`${borderWidth.s} solid ${colors.border}`}
                        boxShadow={shadows.xs}
                        borderRadius={{ xs: radius.ml, md: radius.l }}
                        backgroundColor={colors.background}
                        width={{ xs: sizes.size36, md: sizes.size48 }}
                        height={{ xs: sizes.size36, md: sizes.size48 }}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {breakpoints.get({
                          xs: <Icon.Medium name={iconName} />,
                          md: <Icon.Large name={iconName} />,
                        })}
                      </Block>
                    )}
                  >
                    <>
                      <Body16 strong>{title}</Body16>
                      <Body14 color={colors.muted}>{description}</Body14>
                    </>
                  </List.Item>
                ))}
              </CardBase>
            </Block>
          </Modal.Item.Wrapper>
          <Modal.Item.Footer column>
            <Button.Large
              block
              href={
                jobber?.id
                  ? `${RECURRING_JOBS_NEW_PATH}?jobber_id=${jobber.id}`
                  : RECURRING_JOBS_NEW_PATH
              }
            >
              {polyglot.t("recurring.create_recurring")}
            </Button.Large>
            {refuseUrl && (
              <Button.Large
                block
                href={refuseUrl}
                kind={BUTTON.KIND.SECONDARY}
              >
                {polyglot.t("common.no_thank_you")}
              </Button.Large>
            )}
          </Modal.Item.Footer>
        </Block>
      </Block>
    </Modal.Small>
  );
};

export default RecurringJobsDiscoverModal;
